import { createFileRoute } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { trpc } from '@/lib/trpc';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { PageSeo } from './-components/page-seo';
import { slugs } from './solutions.meta';
import { Partners } from './-components/partners';
import { Hero } from './-components/solutions/hero';
import { SolutionTabs } from './-components/solutions/solution-tabs';
import { SOLUTION_KEYS } from './-components/solutions/solutions-data';
import { ValueProps } from './-components/solutions/value-props';
import { SuccessCases, SUCCESS_CASE_PORTAL_IDS } from './-components/solutions/success-cases';
import { TechPartners } from './-components/solutions/tech-partners';

export const Route = createFileRoute('/www/solutions')({
	// In the URL because the footer links three tabs directly; `v.fallback` so a stale `?solution=` opens
	// the first tab rather than 404ing a static page.
	validateSearch: v.object({
		solution: v.fallback(v.optional(v.picklist(SOLUTION_KEYS)), undefined),
	}),
	loader: ({ context }) => {
		// `prefetchQuery`, not `ensureQueryData`: see the homepage loader.
		context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
		// Warmed so the success-case logos are in the server's HTML, not added after hydration; one batched request.
		for (const portalId of SUCCESS_CASE_PORTAL_IDS) {
			context.queryClient.prefetchQuery(trpc.portal.getAuthor.queryOptions({ portalId, locale: context.locale }));
		}
	},
	component: Component,
});

function PartnersSection() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	// The connector leads into the homepage's next section, not the success stories.
	return <Partners merchants={stats.partnerMerchants} connector={false} />;
}

function Component() {
	const { t } = useLingui();

	return (
		<Layout>
			<Header />
			<PageSeo
				slugs={slugs}
				title={t`Solutions - Worldcoo`}
				description={t`Since 2013, at Worldcoo we build fundraising tools so that companies, their customers and their employees can finance social projects easily, comfortably and with full transparency.`}
			/>
			<Hero />
			<SolutionTabs />
			<ValueProps />
			{/* `connector` matches the real one, or the fallback is 130px taller. */}
			<Suspense fallback={<Partners connector={false} />}>
				<PartnersSection />
			</Suspense>
			<SuccessCases />
			<TechPartners />
			<Footer cta="form" />
		</Layout>
	);
}
